/**
 * EditEdgeCommand - Thin command for editing edge (transition) with undo/redo support
 *
 * Architecture:
 * - Command is THIN - only stores old state for undo/redo
 * - ALL business logic is in FlowCanvasCoordinator
 * - Coordinator handles: validation, ScreenTransitionManager, Canvas updates, Firebase sync
 *
 * Execution flow:
 * 1. Constructor reads current state from Coordinator (BEFORE execute)
 * 2. Command calls coordinator.updateTransition()
 * 3. Coordinator does everything (struct update, Canvas, Firebase)
 *
 * Undo flow:
 * 1. Command calls coordinator.updateTransition() with saved old state
 * 2. Coordinator does everything (struct update, Canvas, Firebase)
 *
 * Sprint: sprint-2
 * Phase: 5 (Edge Commands Refactor - Thin Pattern)
 */

import type { CommandResult } from './Command';
import type { CommandContext } from './CommandContext';

/**
 * Updates that can be applied to an edge
 */
export interface EdgeUpdates {
  targetEntityId?: string;
  label?: string;
}

/**
 * Command: Edit edge (transition) properties
 *
 * This is a THIN command - all logic is delegated to Coordinator
 */
export class EditEdgeCommand {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  private readonly edgeId: string;
  private readonly updates: EdgeUpdates;
  private readonly context: CommandContext;

  // Old state for undo (read in constructor BEFORE execute)
  private readonly oldState: EdgeUpdates;

  constructor(
    edgeId: string,
    updates: EdgeUpdates,
    context: CommandContext
  ) {
    this.id = `edit-edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.edgeId = edgeId;
    this.updates = updates;
    this.context = context;
    this.timestamp = Date.now();

    const updateFields = Object.keys(updates).join(', ');
    this.description = `Edit edge ${edgeId.slice(0, 16)} (${updateFields})`;

    // Read current state BEFORE execute (for undo)
    const allTransitions = this.context.coordinator.getAllTransitions();
    const existingTransition = allTransitions.find(t => t.id === edgeId);
    if (!existingTransition) {
      throw new Error(`Transition ${edgeId} not found`);
    }

    // Store only fields that will be changed
    this.oldState = {};
    if (updates.targetEntityId !== undefined) {
      this.oldState.targetEntityId = existingTransition.targetEntityId;
    }
    if (updates.label !== undefined) {
      this.oldState.label = existingTransition.label;
    }

    console.log('[EditEdgeCommand] Constructor - stored old state:', this.oldState);
  }

  public canExecute(): boolean {
    // Must have at least one update
    return Object.keys(this.updates).length > 0;
  }

  public canUndo(): boolean {
    // Can undo if we have old state
    return Object.keys(this.oldState).length > 0;
  }

  public async execute(): Promise<CommandResult> {
    if (!this.canExecute()) {
      return {
        success: false,
        error: new Error('No updates provided for edge'),
        affectedEntities: {}
      };
    }

    try {
      console.log('[EditEdgeCommand] execute:', {
        edgeId: this.edgeId.slice(0, 16),
        updates: this.updates
      });

      // Coordinator does ALL the work:
      // 1. Finds owning screen
      // 2. Updates transition in screen.struct via ScreenTransitionManager
      // 3. Updates ScreenRegistry
      // 4. Point update Canvas (updateEdge)
      // 5. Persists to Firebase
      await this.context.coordinator.updateTransition(this.edgeId, this.updates);

      console.log('[EditEdgeCommand]   ✓ Updated');

      return {
        success: true,
        affectedEntities: {
          transitions: [this.edgeId]
        }
      };
    } catch (error) {
      console.error('[EditEdgeCommand] ❌ Execution failed:', error);
      return {
        success: false,
        error: error as Error,
        affectedEntities: {}
      };
    }
  }

  public async undo(): Promise<CommandResult> {
    if (!this.canUndo()) {
      return {
        success: false,
        error: new Error('Cannot undo: no original values stored'),
        affectedEntities: {}
      };
    }

    try {
      console.log('[EditEdgeCommand] undo:', {
        edgeId: this.edgeId.slice(0, 16),
        restoringState: this.oldState
      });

      // Coordinator does ALL the work:
      // 1. Finds owning screen
      // 2. Updates transition with old values via ScreenTransitionManager
      // 3. Updates ScreenRegistry
      // 4. Point update Canvas (updateEdge)
      // 5. Persists to Firebase
      await this.context.coordinator.updateTransition(this.edgeId, this.oldState);

      console.log('[EditEdgeCommand]   ✓ Undone');

      return {
        success: true,
        affectedEntities: {
          transitions: [this.edgeId]
        }
      };
    } catch (error) {
      console.error('[EditEdgeCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error as Error,
        affectedEntities: {}
      };
    }
  }

  public async redo(): Promise<CommandResult> {
    // Re-execute with new values
    return this.execute();
  }
}
